import { Dialog, DialogTitle } from "@mui/material";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { addCell, selectLensEditorLensId } from "redux/slices/lensEditor";
import CellEditor from "./CellEditor";
import { Cell, CellType, FormField, TextCellValidationStrategy } from "generated/graphql";
import { useContext, useMemo } from "react";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";
import { CellFormState, CellPreset } from "./types";

interface NewCellDialogProps {
  open: boolean;
  close: () => void;
  field: FormField;
  rank: number;
  preset?: CellPreset;
}

const NewCellDialog = ({ open, close, field, rank, preset }: NewCellDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const lensId = useAppSelector(selectLensEditorLensId);

  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);

  const save = (cell: CellFormState) => {
    dispatch(
      addCell({
        ...(cell as Cell),
        // System name is always going to be present
        systemName: cell.systemName!,
        analyticsExclude: cell.analyticsExclude === true,
      })
    )
      .unwrap()
      .then(() => {
        enqueueSnackbar("Cell added", {
          variant: "success",
        });
        close();
        enqueueLensUpdateTimerSnackbar(lensId);
      })
      .catch((e) => {
        enqueueSnackbar(`Unable to add cell - ${e?.message}`, {
          variant: "error",
        });
      });
  };

  const initialCellFromPreset: CellFormState = useMemo(() => {
    const defaultFormState: CellFormState = {
      exportExclude: false,
      optional: false,
      fieldId: field.id,
      selectOptions: [],
    };
    switch (preset) {
      case undefined:
      case CellPreset.Custom:
        return defaultFormState;
      case CellPreset.SSN:
        return {
          ...defaultFormState,
          name: "SSN",
          systemName: "ssn",
          description: "Social security number in the format (XXX-XX-XXXX)",
          type: CellType.Text,
          validationConfig: {
            strategy: TextCellValidationStrategy.Ssn,
          },
        };
      case CellPreset.PacksPerDay:
        return {
          ...defaultFormState,
          name: "Packs Per Day",
          systemName: "packsperday",
          description: "Packs of cigarettes smoked per day",
          type: CellType.Float,
          validationConfig: {
            minValue: 0,
          },
        };
      case CellPreset.BMI:
        return {
          ...defaultFormState,
          name: "BMI",
          systemName: "bmi",
          description: "Body Mass Index",
          type: CellType.Float,
          validationConfig: {
            minValue: 5,
            maxValue: 200,
          },
        };
    }
  }, [preset, field]);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>New Cell</DialogTitle>
      <div className="flex flex-col gap-2 py-2 px-4">
        <CellEditor field={field} rank={rank} onSave={save} onCancel={close} cell={initialCellFromPreset} />
      </div>
    </Dialog>
  );
};

export default NewCellDialog;
